import { useState } from "react";
import { FIRM_DATABASE } from "../../firmDatabase";
import { C } from "../../lib/colors";
import { useT } from "../LangContext";
import { useAuth } from "../AuthContext";
import { useUserPlan } from "../UserPlanContext";
import HeaderSeal from "./HeaderSeal";
import AuthModal from "../auth/AuthModal";

export function LangToggle({ lang, setLang }) {
  const active = { color: C.brass, fontWeight: 700 };
  const dim    = { color: C.smoke, cursor: "pointer" };
  return (
    <div style={{ fontSize: 12, letterSpacing: 0.2, fontFamily: "var(--mono)" }}
         data-testid="lang-toggle">
      <span style={{ color: C.smoke }}>[ </span>
      <span style={lang === "es" ? active : dim} onClick={() => setLang("es")}
            data-testid="lang-es" role="button" tabIndex={0}>
        {lang === "es" ? "ES" : "es"}
      </span>
      <span style={{ color: C.haze, margin: "0 6px" }}>·</span>
      <span style={lang === "en" ? active : dim} onClick={() => setLang("en")}
            data-testid="lang-en" role="button" tabIndex={0}>
        {lang === "en" ? "EN" : "en"}
      </span>
      <span style={{ color: C.smoke }}> ]</span>
    </div>
  );
}

function PlanBadge({ plan }) {
  const colors = {
    free: C.smoke,
    pro: C.brass,
    annual: C.cinnabar,
  };
  return (
    <span 
      style={{
        fontSize: 9,
        fontFamily: "var(--mono)",
        fontWeight: 600,
        letterSpacing: '0.15em',
        color: colors[plan] || C.smoke,
        padding: '2px 6px',
        border: `1px solid ${colors[plan] || C.smoke}`,
        marginLeft: 8,
      }}
      data-testid="plan-badge"
    >
      {plan?.toUpperCase()}
    </span>
  );
}

function truncateEmail(email) {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const domainParts = domain.split('.');
  const ext = domainParts.pop();
  return `${local.slice(0, 4)}@...${ext}`;
}

export default function Header() {
  const { lang, t, setLang } = useT();
  const { user, loading } = useAuth();
  const { plan, loading: planLoading } = useUserPlan();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const totalFirms = FIRM_DATABASE.filter(f => f.market !== "custom").length;
  const totalPlans = FIRM_DATABASE.reduce((a, f) => a + f.plans.length, 0);

  // Fase 17B · Bloque 3 — Account icon opens the ACCOUNT view via pf:nav.
  // For anonymous / demo users the icon opens the auth modal so they can
  // sign up (per spec). The whole account flow (logout, manage subscription,
  // plans) now lives inside AccountView.
  const openAccount = () => {
    if (user) {
      window.dispatchEvent(new CustomEvent("pf:nav", { detail: { to: "account" } }));
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <header className="pf-header" data-testid="pf-header">
        <div className="pf-header-inner">
          <div className="pf-brand">
            <HeaderSeal />
            <div>
              <div className="pf-brand-mark" data-testid="hero-title">
                PROP<span className="dot">·</span>FORGE
              </div>
              <span className="pf-brand-sub">{t("app_subtitle")}</span>
              <span className="pf-brand-line" />
            </div>
          </div>
          <div className="pf-header-actions">
            <LangToggle lang={lang} setLang={setLang} />
            
            {!loading && (
              <div className="pf-auth" data-testid="pf-auth">
                {user ? (
                  <>
                    <span className="pf-auth-email" data-testid="user-email">
                      {truncateEmail(user.email)}
                    </span>
                    {!planLoading && <PlanBadge plan={plan} />}
                    <button
                      className="pf-auth-btn pf-account-icon"
                      onClick={openAccount}
                      data-testid="header-account-btn"
                      aria-label={t("account_open_aria")}
                      title={t("account_open_aria")}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <span aria-hidden="true" style={{ fontSize: 14 }}>◔</span>
                      <span>{t("tab_account")}</span>
                    </button>
                    {/* Fase 16B · Bloque 5 — Invite link */}
                    <InviteButton t={t} />
                  </>
                ) : (
                  <>
                    <button
                      className="pf-auth-btn pf-account-icon"
                      onClick={openAccount}
                      data-testid="header-account-btn"
                      aria-label={t("account_open_aria")}
                      title={t("account_open_aria")}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <span aria-hidden="true" style={{ fontSize: 14 }}>◔</span>
                    </button>
                    <button 
                      className="pf-auth-btn" 
                      onClick={() => setShowAuthModal(true)}
                      data-testid="signin-btn"
                    >
                      {t('auth_sign_in')}
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="pf-meta">
              {totalFirms} firms <span className="acc">·</span> {totalPlans} plans
            </div>
          </div>
        </div>
      </header>
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
}

// ─── Fase 16B · Bloque 5 — Invite a friend button ─────────────────────────
// 1-click share that prefers Web Share API on mobile, falls back to clipboard
// copy + a toast confirmation. Link: /?ref=invite (anonymous, no per-user code).
function InviteButton({ t }) {
  const [copied, setCopied] = useState(false);
  const handleInvite = async () => {
    const url = `${window.location.origin}/?ref=invite`;
    const shareData = {
      title: "Prop Forge",
      text: t("invite_share_text"),
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch { /* user cancelled — ignore */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard blocked — ignore */ }
  };
  return (
    <button className="pf-auth-btn" onClick={handleInvite}
            data-testid="invite-btn"
            title={t("invite_tooltip")}>
      {copied ? t("invite_copied") : t("invite_label")}
    </button>
  );
}
